import mainBookSummary from "../data/mainBookSummary.json";
import type { OpenPortfolioSummary } from "../api/openPortfolioApi";
import {
  formatPercent,
  paperResults,
  publishedSeriesEndDate,
  PUBLISHED,
} from "../components/Home/landing/samplePortfolio";
import { weeksAhead } from "../components/OpenPortfolio/openPortfolioView";
import {
  MODEL_PORTFOLIO_HEADLINE_DISCLOSURE,
  MODEL_PORTFOLIO_PAST_PERFORMANCE_DISCLAIMER,
  formatModelPortfolioWholePercent,
  modelPortfolioPerformanceHeadlineLine1,
} from "../constants/modelPortfolio";

const STATIC_BOOK = mainBookSummary as OpenPortfolioSummary;

/** Compounded since-inception totals and weekly beat counts from published book data. */
export type PublishedPerformanceHeadline = {
  portfolioTotal: string;
  spTotal: string;
  weeksAhead: number;
  weeksTotal: number;
};

/** Last `weekEnding` in the bundled open-portfolio weekly series. */
export function publishedPerformanceLastWeekEnding(
  summary: OpenPortfolioSummary = STATIC_BOOK,
): string | null {
  return publishedSeriesEndDate(summary);
}

/** e.g. "Through week ending 2026-10-02" */
export function modelPortfolioThroughWeekLabel(
  summary: OpenPortfolioSummary = STATIC_BOOK,
): string {
  const end = publishedPerformanceLastWeekEnding(summary);
  return end ? `Through week ending ${end}` : "Model portfolio";
}

const staticResults = paperResults(STATIC_BOOK);

/** Totals from the bundled static series only (not the API scoreboard gap). */
export const PUBLISHED_STATIC_PERFORMANCE = {
  portfolioTotal: staticResults.portfolioTotal,
  spTotal: staticResults.spTotal,
  portfolioLabel: formatModelPortfolioWholePercent(staticResults.portfolioTotal),
  spLabel: formatModelPortfolioWholePercent(staticResults.spTotal),
  throughWeekEnding: publishedPerformanceLastWeekEnding(STATIC_BOOK),
  throughLabel: modelPortfolioThroughWeekLabel(STATIC_BOOK),
} as const;

/** Same weeks as GrowthPanel: portfolio return beat the S&P that week (`qiReturn > spReturn`). */
export function publishedPerformanceHeadline(
  summary: OpenPortfolioSummary = PUBLISHED,
): PublishedPerformanceHeadline {
  const { portfolioTotal, spTotal } = paperResults(summary);
  const ahead = weeksAhead(summary);
  return {
    portfolioTotal: formatModelPortfolioWholePercent(portfolioTotal),
    spTotal: formatModelPortfolioWholePercent(spTotal),
    weeksAhead: ahead.filter(Boolean).length,
    weeksTotal: ahead.length,
  };
}

export function publishedWeeksAheadLabel(headline: PublishedPerformanceHeadline): string {
  return `beat the S&P 500 in ${headline.weeksAhead} of ${headline.weeksTotal} weeks`;
}

export type ModelPortfolioPerformanceHeadlineLines = {
  line1: string | null;
  line2: string;
  line3: string;
};

/** Three-line public performance headline derived from bundled weekly data. */
export function modelPortfolioPerformanceHeadlineLines(
  summary: OpenPortfolioSummary = STATIC_BOOK,
): ModelPortfolioPerformanceHeadlineLines {
  const { portfolioTotal, spTotal } = paperResults(summary);
  const through = publishedPerformanceLastWeekEnding(summary);
  return {
    line1: through
      ? modelPortfolioPerformanceHeadlineLine1(
          Math.round(portfolioTotal),
          Math.round(spTotal),
          through,
        )
      : null,
    line2: MODEL_PORTFOLIO_HEADLINE_DISCLOSURE,
    line3: MODEL_PORTFOLIO_PAST_PERFORMANCE_DISCLAIMER,
  };
}

export { PUBLISHED as PUBLISHED_STATIC_BOOK } from "../components/Home/landing/samplePortfolio";
